import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Card, Chip, Stack, LinearProgress, Divider, useTheme, IconButton, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSelector, useDispatch } from 'react-redux';
import { Security } from '@mui/icons-material';
import { openModal } from '../../store/reducers/actions';
import FreeBlockModal from 'sections/premiumBlock/FreeBlockModal';

const SegmentDetails = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [isMinimized, setIsMinimized] = useState(false);
  const leftColumnRef = useRef(null);
  const segmentItemRef = useRef(null);

  // Get everything from Redux
  const segments = useSelector((state) => state.icpReducer?.segments || []);
  const selectedSegment = useSelector((state) => state.icpReducer?.selectedSegment);
  // Add these new selectors
  const plan = useSelector((state) => state.proAccountReducer?.plan);
  const isProUser = plan === 'Pro' || plan === 'Trial' || plan === 'Pro+' || plan === 'Enterprise Pro Max' || plan === 'Enterprise Pro';
  const trialWasActivated = useSelector((state) => state.proAccountReducer?.trial_was_activated);
  const isModalOpen = useSelector((state) => state.modalReducer.isOpen);

  // Determine visible and paywall segments
  const regularSegments = segments.slice(0, Math.max(segments.length - 4, 1));
  const paywallSegments = segments.slice(Math.max(segments.length - 4, 1));

  // Important! Keep this effect to handle initial state
  useEffect(() => {
    if (!selectedSegment && segments.length > 0) {
      dispatch({ type: 'SET_SELECTED_SEGMENT', payload: segments[0] });
    }
  }, [segments, selectedSegment, dispatch]);

  // Calculate how many segments can fit when minimized
  useEffect(() => {
    if (isMinimized && leftColumnRef.current && segmentItemRef.current) {
      const leftHeight = leftColumnRef.current.clientHeight;
      const segmentHeight = segmentItemRef.current.clientHeight;
      const maxSegments = Math.floor(leftHeight / segmentHeight);

      // If we can't fit all segments, show as many as we can
      if (segments.length > maxSegments) {
        // Always include the selected segment
        const selectedSegmentIndex = segments.findIndex((s) => s.segment_id === selectedSegment?.segment_id);
        let segmentsToShow = [...segments];

        if (selectedSegmentIndex !== -1) {
          // Remove selected segment from its current position
          segmentsToShow.splice(selectedSegmentIndex, 1);
          // Add it at the beginning
          segmentsToShow.unshift(selectedSegment);
          // Trim to max segments
          segmentsToShow = segmentsToShow.slice(0, maxSegments);
        } else {
          segmentsToShow = segments.slice(0, maxSegments);
        }

        // Use segments directly instead of visibleSegments state
        // The component will use segments array directly
      }
    }
  }, [isMinimized, segments, selectedSegment]);

  const handleSegmentSelect = (segment) => {
    dispatch({ type: 'SET_SELECTED_SEGMENT', payload: segment });
  };

  const getScoreColor = (score) => {
    if (score >= 9) return theme.palette.success.main;
    if (score >= 8) return theme.palette.success.light;
    if (score >= 7) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getConfidenceLevel = (score) => {
    if (score >= 8.5) return 'high';
    if (score >= 8) return 'good';
    return 'moderate';
  };

  // Add trial click handler
  const handleTrialClick = () => {
    dispatch(
      openModal({
        title: 'Get access to all segments',
        subtitle: "Upgrade to Pro to view all segments. Don't miss out on accessing more insights!",
        trial: !trialWasActivated
      })
    );
  };

  return (
    <>
      {!isProUser && isModalOpen && <FreeBlockModal />}
      <Stack spacing={3}>
        <Grid container spacing={3}>
          {/* Left Column - Details */}
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3, borderRadius: 8 }} ref={leftColumnRef}>
              {/* Header */}
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
                <Typography variant="h3">{selectedSegment?.name}</Typography>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={selectedSegment?.customer_type}
                    sx={{
                      backgroundColor: selectedSegment?.customer_type === 'B2B' ? 'rgba(0, 171, 85, 0.08)' : 'rgba(32, 101, 209, 0.08)',
                      color: selectedSegment?.customer_type === 'B2B' ? 'rgb(0, 120, 60)' : 'rgb(24, 72, 148)'
                    }}
                  />
                  {isProUser && (
                    <IconButton onClick={() => setIsMinimized(!isMinimized)} size="small">
                      {isMinimized ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                    </IconButton>
                  )}
                </Stack>
              </Stack>

              {/* Summary Section - Always visible */}
              <Box mb={3}>
                <Typography variant="h4" color="primary" gutterBottom>
                  Segment Summary
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                  {`${
                    selectedSegment?.core_characteristic
                  } who need to ${selectedSegment?.JTBD.toLowerCase()}. Their key priorities are ${selectedSegment?.decision_drivers.join(
                    ', '
                  )}, and they typically prefer ${selectedSegment?.purchase_behavior.purchase_preference}.`}
                </Typography>
              </Box>

              {/* Detailed content - Only visible when not minimized */}
              {!isMinimized && (
                <>
                  <Divider sx={{ mb: 3 }} />
                  {/* Two Column Layout */}
                  <Grid container spacing={3}>
                    {/* Left Column */}
                    <Grid item xs={12} md={6}>
                      {/* Core Characteristics */}
                      <Box mb={3}>
                        <Typography variant="h5" color="primary" gutterBottom>
                          Core Characteristics
                        </Typography>
                        <Typography variant="body1">{selectedSegment?.core_characteristic}</Typography>
                      </Box>

                      {/* Job to be Done */}
                      <Box mb={3}>
                        <Typography variant="h5" color="primary" gutterBottom>
                          Job to be Done (JTBD)
                        </Typography>
                        <Typography variant="body1">{selectedSegment?.JTBD}</Typography>
                      </Box>

                      {/* Purchase Behavior */}
                      <Box>
                        <Typography variant="h5" color="primary" gutterBottom>
                          Purchase Behavior
                        </Typography>
                        <Stack spacing={1}>
                          <Typography variant="body1">
                            <strong>RFM Tier:</strong> {selectedSegment?.purchase_behavior.RFM_tier}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Purchase Preference:</strong> {selectedSegment?.purchase_behavior.purchase_preference}
                          </Typography>
                        </Stack>
                      </Box>
                    </Grid>

                    {/* Right Column */}
                    <Grid item xs={12} md={6}>
                      {/* Decision Drivers */}
                      <Box mb={3}>
                        <Typography variant="h5" color="primary" gutterBottom>
                          Decision Drivers
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                          {selectedSegment?.decision_drivers.map((driver) => (
                            <Chip key={driver} label={driver} variant="outlined" />
                          ))}
                        </Stack>
                      </Box>

                      {/* Validity Score */}
                      <Box>
                        <Typography variant="h5" color="primary" gutterBottom>
                          Segment Validity Assessment
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body1">Validity Score</Typography>
                          <Typography variant="body1" sx={{ color: getScoreColor(selectedSegment?.validity_score) }}>
                            {selectedSegment?.validity_score}/10
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={selectedSegment?.validity_score * 10}
                          sx={{ mb: 1, height: 8, borderRadius: 1 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          This {selectedSegment?.customer_type} segment has a validity score of {selectedSegment?.validity_score},
                          indicating{' '}
                          <span style={{ color: getScoreColor(selectedSegment?.validity_score) }}>
                            {getConfidenceLevel(selectedSegment?.validity_score)} confidence
                          </span>{' '}
                          in the segmentation analysis.
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}
            </Card>
          </Grid>

          {/* Right Column - Segment Selector */}
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ mb: 0.25 }}>
                  {isMinimized ? 'Visible Segments' : 'Segments'}
                </Typography>
                {!isMinimized && (
                  <Typography variant="body2" color="text.secondary">
                    Select a segment to view details
                  </Typography>
                )}
              </Box>

              <Stack spacing={2}>
                {/* Regular segments */}
                {regularSegments.map((seg, index) => (
                  <motion.div
                    key={seg.segment_id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    ref={index === 0 ? segmentItemRef : null}
                  >
                    <Card
                      sx={{
                        cursor: 'pointer',
                        position: 'relative',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: selectedSegment?.segment_id === seg.segment_id ? 'primary.main' : 'divider',
                        bgcolor: selectedSegment?.segment_id === seg.segment_id ? 'primary.lighter' : 'background.paper',
                        '&:hover': {
                          bgcolor: 'primary.lighter'
                        }
                      }}
                      onClick={() => handleSegmentSelect(seg)}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1 }}>
                        <Stack spacing={0.25} sx={{ flex: 1 }}>
                          <Typography variant="body2">{seg.name}</Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={seg.customer_type}
                              size="small"
                              sx={{
                                backgroundColor: seg.customer_type === 'B2B' ? 'rgba(0, 171, 85, 0.08)' : 'rgba(32, 101, 209, 0.08)',
                                color: seg.customer_type === 'B2B' ? 'rgb(0, 120, 60)' : 'rgb(24, 72, 148)'
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Score: {seg.validity_score}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Card>
                  </motion.div>
                ))}

                {/* Single paywall card for remaining segments */}
                {!isProUser && paywallSegments.length > 0 && (
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ marginTop: '16px' }}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        position: 'relative',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        overflow: 'hidden',
                        minHeight: '100px'
                      }}
                      onClick={handleTrialClick}
                    >
                      {/* Blurred content showing one of the segments as example */}
                      <Box
                        sx={{
                          filter: 'blur(3px)',
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1 }}>
                          <Stack spacing={0.25} sx={{ flex: 1 }}>
                            <Typography variant="body2">{paywallSegments[0].name}</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={paywallSegments[0].customer_type}
                                size="small"
                                sx={{
                                  backgroundColor:
                                    paywallSegments[0].customer_type === 'B2B' ? 'rgba(0, 171, 85, 0.08)' : 'rgba(32, 101, 209, 0.08)',
                                  color: paywallSegments[0].customer_type === 'B2B' ? 'rgb(0, 120, 60)' : 'rgb(24, 72, 148)'
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                Score: {paywallSegments[0].validity_score}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Box>

                      {/* Overlay with upgrade text */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(2px)',
                          p: 2,
                          textAlign: 'center'
                        }}
                      >
                        <Stack spacing={1} alignItems="center">
                          <Security sx={{ fontSize: 20, color: 'primary.main' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {!trialWasActivated ? 'Start Free Trial' : 'Upgrade Now'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            to access {paywallSegments.length} more segments
                          </Typography>
                        </Stack>
                      </Box>
                    </Card>
                  </motion.div>
                )}

                {/* Pro user segments */}
                {isProUser &&
                  paywallSegments.map((seg) => (
                    <motion.div key={seg.segment_id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          position: 'relative',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: selectedSegment?.segment_id === seg.segment_id ? 'primary.main' : 'divider',
                          bgcolor: selectedSegment?.segment_id === seg.segment_id ? 'primary.lighter' : 'background.paper',
                          '&:hover': {
                            bgcolor: 'primary.lighter'
                          }
                        }}
                        onClick={() => handleSegmentSelect(seg)}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1 }}>
                          <Stack spacing={0.25} sx={{ flex: 1 }}>
                            <Typography variant="body2">{seg.name}</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={seg.customer_type}
                                size="small"
                                sx={{
                                  backgroundColor: seg.customer_type === 'B2B' ? 'rgba(0, 171, 85, 0.08)' : 'rgba(32, 101, 209, 0.08)',
                                  color: seg.customer_type === 'B2B' ? 'rgb(0, 120, 60)' : 'rgb(24, 72, 148)'
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                Score: {seg.validity_score}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Card>
                    </motion.div>
                  ))}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </>
  );
};

export default SegmentDetails;
